import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useChannels } from "@/api/queries/channels";
import { useAgents } from "@/api/queries/agents";
import { useCreateBinding } from "@/api/mutations/channelBindings";

// #182: Type-spezifische Eingabe-Hilfe (Placeholder + Hint). Server validiert
// trotzdem via Channel-Resolve — clientseitig nur UX, kein Trust.
const FIELD_HINTS: Record<string, { placeholder: string; hint: string }> = {
  whatsapp: {
    placeholder: "+41 78 763 35 54",
    hint: "Telefonnummer im internationalen Format (Channel-Plugin normalisiert zu LID-JID)",
  },
  telegram: {
    placeholder: "@max_muster oder 123456789",
    hint: "Username (mit @) oder numerische Chat-ID",
  },
  email: {
    placeholder: "max@firma.ch",
    hint: "Vollständige Email-Adresse",
  },
  web: {
    placeholder: "session-id",
    hint: "Web-Bindings entstehen normalerweise automatisch beim ersten Login",
  },
};

const FALLBACK_VALUE = "__fallback__";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
}

export function CreateBindingDialog({ open, onOpenChange, userId }: Props) {
  const channelsQuery = useChannels();
  const agentsQuery = useAgents();
  const channels = channelsQuery.data ?? [];
  const allAgents = agentsQuery.data ?? [];
  const [channelId, setChannelId] = useState<string>("");
  const [externalId, setExternalId] = useState("");
  const [label, setLabel] = useState("");
  // #182: Agent-Picker. FALLBACK_VALUE = null (kein expliziter Target).
  const [agentSelection, setAgentSelection] = useState<string>(FALLBACK_VALUE);
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = useCreateBinding(userId);

  // Eligible Agents = owned by this User + alle service-agents
  const eligibleAgents = useMemo(
    () =>
      allAgents.filter(
        (a) => a.ownerId === userId || a.type === "service-agent",
      ),
    [allAgents, userId],
  );

  // #182: Wenn der User exakt 1 eigenen user-agent und keine service-agents hat,
  // ist die Fallback-Logik aequivalent — Picker auf den Agent vorbelegen ist
  // ehrlicher (explizit > implizit).
  useEffect(() => {
    if (!open) return;
    const owned = allAgents.filter((a) => a.ownerId === userId);
    const services = allAgents.filter((a) => a.type === "service-agent");
    if (owned.length === 1 && services.length === 0) {
      setAgentSelection(owned[0]!.id);
    }
  }, [open, allAgents, userId]);

  const selectedChannel = useMemo(
    () => channels.find((c) => c.id === channelId),
    [channels, channelId],
  );
  const fieldHint = selectedChannel
    ? FIELD_HINTS[selectedChannel.type] ?? { placeholder: "externe ID", hint: "" }
    : { placeholder: "Zuerst Channel wählen", hint: "" };

  function reset() {
    setChannelId("");
    setExternalId("");
    setLabel("");
    setAgentSelection(FALLBACK_VALUE);
    setServerError(null);
  }

  function handleClose(o: boolean) {
    if (!o) reset();
    onOpenChange(o);
  }

  function handleSubmit() {
    setServerError(null);
    if (!channelId || !externalId.trim()) {
      setServerError("Channel und externe ID sind Pflichtfelder");
      return;
    }
    const agentId = agentSelection === FALLBACK_VALUE ? null : agentSelection;
    mutation.mutate(
      {
        userId,
        channelId,
        externalId: externalId.trim(),
        label: label.trim() || undefined,
        agentId,
      },
      {
        onSuccess: () => {
          toast.success("Binding angelegt (unverified — Admin muss verifyen)");
          onOpenChange(false);
        },
        onError: (err) => {
          const msg = (err as Error).message ?? "Unbekannter Fehler";
          setServerError(msg);
          toast.error(`Anlegen fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="create-binding-dialog">
        <DialogHeader>
          <DialogTitle>Neue Channel-Binding</DialogTitle>
          <DialogDescription>
            User × Channel × externe Identität → optional spezifischer Agent. Verify-Schritt erfolgt nach dem Anlegen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Channel</Label>
            <Select value={channelId} onValueChange={setChannelId}>
              <SelectTrigger data-testid="binding-field-channel">
                <SelectValue placeholder="Channel wählen…" />
              </SelectTrigger>
              <SelectContent>
                {channels
                  // #185: enrolled/online/offline alle akzeptabel als Target — nur disabled raus.
                  .filter((c) => c.status !== "disabled")
                  .map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} ({c.type})
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="binding-external-id">Externe Identität</Label>
            <Input
              id="binding-external-id"
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              placeholder={fieldHint.placeholder}
              data-testid="binding-field-external-id"
              disabled={!channelId}
            />
            {fieldHint.hint && <div className="text-xs text-overlay0">{fieldHint.hint}</div>}
          </div>

          <div className="space-y-1">
            <Label>Agent-Target</Label>
            <Select value={agentSelection} onValueChange={setAgentSelection}>
              <SelectTrigger data-testid="binding-field-agent">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FALLBACK_VALUE}>— Fallback (Master leitet ab)</SelectItem>
                {eligibleAgents.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.id} ({a.type === "service-agent" ? "service" : "owned"})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-xs text-overlay0">
              „Fallback" = wenn der User genau 1 Agent hat, routet Master automatisch dahin. Bei Multi-Persona oder Service-Bot: hier explizit den Agent wählen.
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="binding-label">Label (optional)</Label>
            <Input
              id="binding-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="z.B. Privat-WhatsApp"
              data-testid="binding-field-label"
            />
          </div>
        </div>

        {serverError && (
          <div className="text-sm text-red" data-testid="binding-server-error">
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} data-testid="binding-cancel">
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            data-testid="binding-submit"
          >
            {mutation.isPending ? "…" : "Anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
